import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { PoundSterling, HandHeart, AlertTriangle, TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function OwnerValueTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiClient.get("/dashboard/value-tile");
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading && !data) {
    return (
      <Card className="border-slate-200" data-testid="value-tile-loading">
        <CardContent className="p-4 text-sm text-slate-500">Loading value tile…</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const trendIcon = data.cancellationsTrend === "up" ? TrendingUp
                  : data.cancellationsTrend === "down" ? TrendingDown
                  : Minus;
  const TrendIcon = trendIcon;
  const trendColor = data.cancellationsTrend === "up" ? "text-red-500"
                   : data.cancellationsTrend === "down" ? "text-emerald-600"
                   : "text-slate-500";
  const trendLabel = data.cancellationsChangePct == null
    ? `${data.cancellationsChange > 0 ? "+" : ""}${data.cancellationsChange}`
    : `${data.cancellationsChangePct > 0 ? "+" : ""}${data.cancellationsChangePct}%`;

  return (
    <Card className="border-slate-200 bg-gradient-to-br from-indigo-50/50 to-white" data-testid="owner-value-tile">
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading font-semibold text-slate-900">Owner Value — Last 30 Days</h3>
          <span className="text-xs text-slate-500">rolling window</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3" data-testid="value-tile-recovered">
            <div className="flex items-center gap-1.5 text-[11px] uppercase text-emerald-700 font-semibold">
              <PoundSterling className="h-3.5 w-3.5" /> Recovered
            </div>
            <div className="mt-1 font-heading font-black text-2xl text-emerald-700">£{Number(data.recovered30d || 0).toLocaleString()}</div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-3" data-testid="value-tile-gift-aid">
            <div className="flex items-center gap-1.5 text-[11px] uppercase text-amber-700 font-semibold">
              <HandHeart className="h-3.5 w-3.5" /> Gift Aid
            </div>
            <div className="mt-1 font-heading font-black text-2xl text-amber-700">£{Number(data.giftAid30d || 0).toLocaleString()}</div>
            <div className="text-[10px] text-amber-700/70 mt-0.5">{data.giftAidEligible} eligible donors</div>
          </div>
          <div className="rounded-lg bg-red-50 border border-red-100 p-3" data-testid="value-tile-failed-open">
            <div className="flex items-center gap-1.5 text-[11px] uppercase text-red-700 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" /> Open Failures
            </div>
            <div className="mt-1 font-heading font-black text-2xl text-red-700">{data.failedOpen}</div>
            <div className="text-[10px] text-red-700/70 mt-0.5">awaiting recovery</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="value-tile-cancellations">
            <div className="flex items-center gap-1.5 text-[11px] uppercase text-slate-700 font-semibold">
              <TrendIcon className={`h-3.5 w-3.5 ${trendColor}`} /> Cancellations
            </div>
            <div className="mt-1 font-heading font-black text-2xl text-slate-900">{data.cancellations30d}</div>
            <div className={`text-[10px] mt-0.5 ${trendColor}`}>{trendLabel} vs prior 30d</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
